import {
  ApplyFn,
  change,
  ChangeState,
  createChangeState,
} from "@greeter/changes";
import { ActivePeriod } from "@greeter/core";
import { createFetchMachine, FetchActor } from "@greeter/xstate-util";
import { assign, setup } from "xstate";
import { addDays } from "date-fns";
import { LenientDatePeriod, TimeOfDay, withTimeOfDay } from "@greeter/date";
import { ActivePeriodsEditorApiRequirements } from "./ActivePeriodsEditor";

export type ActivePeriodsChange =
  | { type: "setStartsAt"; data: { id: string; date: Date } }
  | { type: "setEndsAt"; data: { id: string; date: Date } }
  | { type: "add"; data: { id: string } }
  | { type: "delete"; data: { id: string } };

function setupApply(
  context: ActivePeriodsEditorContext
): ApplyFn<Map<string, ActivePeriod>, ActivePeriodsChange> {
  return function apply(m, c) {
    switch (c.type) {
      case "add": {
        const latest = Array.from(m.values())
          .sort((a, b) => Number(b.period.from) - Number(a.period.from))
          .at(0);
        const from = latest
          ? withTimeOfDay(
              addDays(latest.period.to, 1),
              TimeOfDay.fromDate(latest.period.from)
            )
          : new Date();
        const to = latest ? addDays(latest.period.to, 1) : new Date();
        m.set(c.data.id, {
          id: c.data.id,
          period: new LenientDatePeriod({ from: from, to: to }),
          productId: context.productId,
        });
        break;
      }

      case "delete": {
        m.delete(c.data.id);
        break;
      }

      case "setStartsAt": {
        const activePeriod = m.get(c.data.id);
        if (activePeriod) {
          activePeriod.period.from = c.data.date;
        }
        break;
      }

      case "setEndsAt": {
        const activePeriod = m.get(c.data.id);
        if (activePeriod) {
          activePeriod.period.to = c.data.date;
        }
        break;
      }
    }
  };
}

export type EditorMode = "update" | "create";

export type ActivePeriodsEvent =
  | { type: "internal.setActivePeriods"; data: Array<ActivePeriod> }
  | { type: "change"; data: ActivePeriodsChange }
  | { type: "open" }
  | { type: "close" }
  | { type: "fetchActivePeriods" };

export type ActivePeriodsEditorContext = {
  changes: ChangeState<Map<string, ActivePeriod>, ActivePeriodsChange>;
  productId: string;
  mode: EditorMode;

  specialOpeningHoursRef: FetchActor<
    ActivePeriodsEditorApiRequirements["fetchSpecialOpeningHours"]
  >;
  venueRef: FetchActor<ActivePeriodsEditorApiRequirements["fetchVenue"]>;
  activePeriodsRef?: FetchActor<
    ActivePeriodsEditorApiRequirements["fetchActivePeriods"]
  >;
};

export const activePeriodsEditorMachine = setup({
  types: {} as {
    events: ActivePeriodsEvent;
    context: ActivePeriodsEditorContext;
    input: {
      productId: string;
      mode: "create" | "update";
      api: ActivePeriodsEditorApiRequirements;
    };
  },
}).createMachine({
  context({ input, spawn, self }) {
    const activePeriodsRef =
      input.mode === "update"
        ? spawn(
            createFetchMachine(input.api.fetchActivePeriods.bind(input.api))
          )
        : undefined;

    if (activePeriodsRef && input.productId) {
      activePeriodsRef?.send({ type: "fetch", data: [input.productId] });
    }
    activePeriodsRef?.on("fetched", ({ data }) =>
      self.send({ type: "internal.setActivePeriods", data })
    );

    const venueRef = spawn(
      createFetchMachine(input.api.fetchVenue.bind(input.api))
    );
    venueRef.send({ type: "fetch", data: [] });
    const specialOpeningHoursRef = spawn(
      createFetchMachine(input.api.fetchSpecialOpeningHours.bind(input.api))
    );
    specialOpeningHoursRef.send({ type: "fetch", data: [] });

    return {
      changes: createChangeState(new Map<string, ActivePeriod>()),
      productId: input.productId,
      mode: input.mode,

      activePeriodsRef,
      venueRef,
      specialOpeningHoursRef,
    };
  },
  on: {
    change: {
      actions: assign(({ context, event }) => {
        return {
          changes: {
            ...change(context.changes, setupApply(context), event.data),
          },
        };
      }),
    },
    open: ".opened",
    close: ".closed",
    fetchActivePeriods: {
      actions: function ({ context }) {
        context.activePeriodsRef?.send({
          type: "fetch",
          data: [context.productId],
        });
      },
    },
    "internal.setActivePeriods": {
      actions: assign(({ event }) => ({
        changes: createChangeState(
          event.data.reduce((acc, curr) => {
            acc.set(curr.id, curr as ActivePeriod);
            return acc;
          }, new Map<string, ActivePeriod>())
        ),
      })),
    },
  },
  initial: "closed",
  states: {
    closed: {},
    opened: {},
  },
});
